/**
 * COMMENT_ON_N_PHOTOS task template - shows critique session with progress
 * Feature: 651-n-comments-task
 *
 * @module pages/tasks/components/templates/CommentOnNPhotosTaskTemplate
 */

import { useQuery } from '@apollo/client';
import { Text } from '@/components/atoms/Text';
import { AdaptivePhoto } from '@/components/photos/AdaptivePhoto';
import { TaskDebugPanel } from '@/components/composed/TaskDebugPanel';
import { TaskActions } from './TaskActions';
import { getStatusColor } from '../../utils';
import { GET_CRITIQUE_SESSION_PREVIEW } from '../../graphql/queries';
import type { TaskTemplateProps, CritiqueSessionPreviewData } from '../../types';

export function CommentOnNPhotosTaskTemplate({ task, onNavigate, onComplete, onDismiss, onDelete, isUpdating, debugMode }: TaskTemplateProps) {
  // Fetch critique session for title and hero photo
  const { data: sessionData, loading: sessionLoading } = useQuery<CritiqueSessionPreviewData>(
    GET_CRITIQUE_SESSION_PREVIEW,
    {
      variables: { id: task.targetId || 'skip-placeholder' },
      skip: !task.targetId,
      fetchPolicy: 'cache-first',
    }
  );

  const sessionTitle = sessionData?.critiqueSession?.title || 'Critique Session';
  const collection = sessionData?.critiqueSession?.collection;

  // Get hero photo with fallback chain
  const heroPhoto = collection?.systemHeroPhoto || collection?.heroPhoto || collection?.items?.items?.[0]?.photo;

  // Build the task title: "Comment on N photos in X"
  const targetCount = task.progressRequired ?? 0;
  const taskTitle = `Comment on ${targetCount} photo${targetCount !== 1 ? 's' : ''} in ${sessionTitle}`;

  return (
    <div className="bg-surface-1 border border-border rounded-lg p-4 hover:border-primary/50 transition-colors">
      <div className="flex items-start gap-4">
        {/* Hero Photo Thumbnail */}
        <div className="w-16 h-16 bg-surface-2 rounded-lg overflow-hidden flex-shrink-0 relative">
          {sessionLoading ? (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : heroPhoto ? (
            <AdaptivePhoto
              photo={heroPhoto}
              alt={sessionTitle}
              className="w-full h-full"
              imgClassName="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
          {/* Status indicator overlay */}
          {task.status === 'completed' && (
            <div className="absolute inset-0 bg-success/30 flex items-center justify-center">
              <svg className="w-6 h-6 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
          {/* Progress bar */}
          {task.status === 'pending' && task.progressPercent !== null && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/30">
              <div
                className="h-full bg-amber-500 transition-all"
                style={{ width: `${Math.min(100, task.progressPercent)}%` }}
              />
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-sm font-medium text-foreground">{taskTitle}</span>
            <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusColor(task.status)}`}>
              {task.status}
            </span>
          </div>

          {/* Progress indicator */}
          {task.hasProgress && task.progressRequired && (
            <div className="mb-2">
              <div className="h-1.5 bg-surface-2 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-500 rounded-full transition-all"
                  style={{ width: `${task.progressPercent ?? 0}%` }}
                />
              </div>
              <Text className="text-xs text-muted mt-1">
                {task.progressCurrent} of {task.progressRequired} photos commented
              </Text>
            </div>
          )}

          {/* Metadata */}
          <div className="text-xs text-muted">
            <span>{new Date(task.createdAt).toLocaleTimeString()}</span>
          </div>

          {/* Debug Panel */}
          {debugMode && <TaskDebugPanel task={task} />}
        </div>

        {/* Actions */}
        <TaskActions
          task={task}
          onNavigate={onNavigate}
          onComplete={onComplete}
          onDismiss={onDismiss}
          onDelete={onDelete}
          isUpdating={isUpdating}
        />
      </div>
    </div>
  );
}
